import dayjs from "dayjs";
import React from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";

import { AccountType } from "../../../models/AccountData";
import { LocationData } from "../../../models/LocationData";
import {
  CollectLocationDataType,
  EncodeType,
} from "../../../models/LocationDataDownload";
import DataSvc from "../../../services/dataSvc";
import { buildCsvFilename, downloadCsv } from "../../../utils/csvUtils";
import { OptionalRender } from "../../Common/OptionalRender";

import "./styles.scss";

type IFacilityDataDownloadProps = {
  locationData: LocationData;
};

export const FacilityDataDownload: React.FunctionComponent<
  IFacilityDataDownloadProps
> = ({ locationData }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityDetailsPage.facilityDataDownload.${key}`);

  const [isDownloading, setIsDownloading] = React.useState<{
    utf8: boolean;
    shiftJis: boolean;
  }>({
    utf8: false,
    shiftJis: false,
  });

  const [isDownloadingProcessed, setIsDownloadingProcessed] = React.useState<{
    utf8: boolean;
    shiftJis: boolean;
  }>({
    utf8: false,
    shiftJis: false,
  });

  const extractDateRange = (reviews: any[]) => {
    const dates = reviews
      .map((review) => review.review_date)
      .filter(Boolean)
      .map((date: string) => dayjs(date))
      .filter((date) => date.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf());

    if (dates.length === 0) {
      return {};
    }

    return {
      startDate: dates[0].format("YYYY-MM-DD"),
      endDate: dates[dates.length - 1].format("YYYY-MM-DD"),
    };
  };

  const handleDownload = async (encoding: EncodeType) => {
    const encodingKey = encoding === EncodeType.UTF8 ? "utf8" : "shiftJis";

    setIsDownloading((prev) => ({ ...prev, [encodingKey]: true }));

    try {
      const reviews = await DataSvc.downloadLocationData({
        location_id: locationData.id,
        data_type: CollectLocationDataType.reviews,
      });

      if (!reviews || reviews.length === 0) {
        console.warn("No reviews available");
        setIsDownloading((prev) => ({ ...prev, [encodingKey]: false }));
        return;
      }

      // Create CSV content with specified fields
      const header = [
        "Review Date",
        "Review Id",
        "Reviewer Name",
        "Rating",
        "Comment",
        "Reply Date",
        "Reply Comment",
      ].join(",");

      const csvRows = reviews.map((review: any) => {
        const reviewDate = review.review_date
          ? dayjs(review.review_date).format("YYYY-MM-DD")
          : "";
        const reviewId = review.review_id || "";
        const reviewerName = review.reviewer_name
          ? `"${review.reviewer_name.replace(/"/g, '""')}"`
          : "";
        const rating =
          review.rating !== null && review.rating !== undefined
            ? String(review.rating)
            : "";
        const comment = review.comment
          ? `"${review.comment.replace(/"/g, '""')}"`
          : "";
        const replyDate = review.review_reply_update_time
          ? dayjs(review.review_reply_update_time).format("YYYY-MM-DD")
          : "";
        const replyComment = review.review_reply
          ? `"${review.review_reply.replace(/"/g, '""')}"`
          : "";

        return [
          reviewDate,
          reviewId,
          reviewerName,
          rating,
          comment,
          replyDate,
          replyComment,
        ].join(",");
      });

      const rows = [header, ...csvRows];
      const { startDate, endDate } = extractDateRange(reviews);
      const organizationName = locationData.organizations?.[0]?.name;
      const filename = buildCsvFilename(
        {
          startDate,
          endDate,
          organizationName,
          facilityName: locationData.name,
          facilityId: locationData.id,
          contentDescription: "reviews",
        },
        _t
      );

      // Use csvUtils downloadCsv function
      const forWindows = encoding === EncodeType.SHIFT_JIS;
      downloadCsv(rows, filename, forWindows);

      ReactGA.event({
        category: "facility_details",
        action: "Download Facility Data",
        label: `${CollectLocationDataType.reviews}_${encoding}`,
      });
    } catch (error) {
      console.error(`Download failed for encoding ${encoding}:`, error);
    } finally {
      setIsDownloading((prev) => ({ ...prev, [encodingKey]: false }));
    }
  };

  const handleDownloadProcessed = async (encoding: EncodeType) => {
    const encodingKey = encoding === EncodeType.UTF8 ? "utf8" : "shiftJis";
    setIsDownloadingProcessed((prev) => ({ ...prev, [encodingKey]: true }));

    try {
      const reviews = await DataSvc.getProcessedReviews(
        String(locationData.id)
      );

      if (!reviews || reviews.length === 0) {
        console.warn("No processed reviews available");
        setIsDownloadingProcessed((prev) => ({
          ...prev,
          [encodingKey]: false,
        }));
        return;
      }

      // Create CSV content with specified fields
      const header = [
        "Review Date",
        "Language",
        "Rating",
        "Comment",
        "Translation",
        "ai_sentiment",
      ].join(",");

      const csvRows = reviews.map((review: any) => {
        const reviewDate = review.review_date
          ? new Date(review.review_date).toISOString().split("T")[0]
          : "";
        const language = review.language || "";
        const rating = review.rating || "";
        const comment = review.comment
          ? `"${review.comment.replace(/"/g, '""')}"`
          : "";
        const translation = review.translation
          ? `"${review.translation.replace(/"/g, '""')}"`
          : "";
        const aiSentiment =
          review.ai_sentiment || review.processed_review?.ai_sentiment || "";
        const aiSentimentEscaped = aiSentiment
          ? `"${aiSentiment.replace(/"/g, '""')}"`
          : "";

        return [
          reviewDate,
          language,
          rating,
          comment,
          translation,
          aiSentimentEscaped,
        ].join(",");
      });

      const rows = [header, ...csvRows];
      const { startDate, endDate } = extractDateRange(reviews);
      const organizationName = locationData.organizations?.[0]?.name;
      const filename = buildCsvFilename(
        {
          startDate,
          endDate,
          organizationName,
          facilityName: locationData.name,
          facilityId: locationData.id,
          contentDescription: "processed_reviews",
        },
        _t
      );

      // Use csvUtils downloadCsv function
      const forWindows = encoding === EncodeType.SHIFT_JIS;
      downloadCsv(rows, filename, forWindows);

      ReactGA.event({
        category: "facility_details",
        action: "Download Processed Reviews",
        label: `${locationData.id}_${encoding}`,
      });
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloadingProcessed((prev) => ({ ...prev, [encodingKey]: false }));
    }
  };

  return (
    <OptionalRender mustBeOneOfRoles={[AccountType.ADMIN]}>
      <div className="detail-group facility-data-download">
        <div className="group-title">
          {t("section_name")}
          <div className="line"></div>
        </div>
        <small className="section-hint">{t("section_hint")}</small>

        <div className="white-border-panel download-section">
          <div className="download-item">
            <div className="download-item-content">
              <h4 className="download-title">{t("google_reviews_title")}</h4>
              <p className="download-description">{t("google_reviews_hint")}</p>
              <div className="download-buttons">
                <button
                  className="btn btn-border"
                  onClick={() => handleDownload(EncodeType.UTF8)}
                  disabled={isDownloading.utf8}
                >
                  {isDownloading.utf8 ? t("downloading") : t("download")}
                </button>
                <button
                  className="btn btn-border"
                  onClick={() => handleDownload(EncodeType.SHIFT_JIS)}
                  disabled={isDownloading.shiftJis}
                >
                  {isDownloading.shiftJis
                    ? t("downloading")
                    : t("download_jis")}
                </button>
              </div>
            </div>
          </div>

          <div className="download-item">
            <div className="download-item-content">
              <h4 className="download-title processed-reviews">
                {t("downloadProcessedReviews.title")}
              </h4>
              <p className="download-description">
                {t("downloadProcessedReviews.description")}
              </p>
              <div className="download-buttons">
                <button
                  className="btn btn-border"
                  onClick={() => handleDownloadProcessed(EncodeType.UTF8)}
                  disabled={isDownloadingProcessed.utf8}
                >
                  {isDownloadingProcessed.utf8
                    ? t("downloading")
                    : t("download")}
                </button>
                <button
                  className="btn btn-border"
                  onClick={() => handleDownloadProcessed(EncodeType.SHIFT_JIS)}
                  disabled={isDownloadingProcessed.shiftJis}
                >
                  {isDownloadingProcessed.shiftJis
                    ? t("downloading")
                    : t("download_jis")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OptionalRender>
  );
};
